import React from 'react';
import { Scrollbars } from 'rc-scrollbars';
import classnames from 'classnames';
import { inject, observer } from 'mobx-react';
import { useIntl, getLocale } from 'umi';
import { Icon, Tooltip, Avatar, message } from '@/components';
import PersonLabel from '@/components/PersonLabel';
import { DEVICE_TYPE, MemberStruct } from '@/types/meeting';
import { RootStore, focused, reaction } from '@/models';
import { InterpretationStatus } from '@/models/simultaneousInterpretation';
import * as API from '@/api';
import Style from './index.less';
import { HandUpStatus } from '../../EmoJ';
import HoverIcon from '../../BarragePanel/components/HoverIcon';
export enum RoleType {
  HOST = 0,
  ATTENDEE = 1,
  GUEST = 2,
  SIP = 3,
  COHOST = 4,
}
interface PeoplePanelProps extends Partial<Pick<RootStore, 'user' | 'members' | 'meeting'>> {
  visible: boolean;
  talkingIds: string[];
  RTCInstance?: RootStore['rtc'];
  className?: string;
  simultaneous?: RootStore['simultaneousInterpretation'];
}

let toastKey: any = null;
const PeoplePanel: React.FC<PeoplePanelProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    RTCInstance: store.rtc,
    user: store.user,
    members: store.members,
    simultaneous: store.simultaneousInterpretation,
  };
})(
  observer((props: PeoplePanelProps) => {
    const { visible, meeting, user, RTCInstance, members, simultaneous, className } = props;
    const { formatMessage } = useIntl();
    const renderThumb = ({ style, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
      const thumbStyle = {
        backgroundColor: isPad ? `rgba(255, 255, 255, 0.5)` : `rgba(255, 255, 255, 0.1)`,
        borderRadius: '5px',
        display: 'block',
      };
      return <div style={{ ...style, ...thumbStyle }} {...props} />;
    };

    const myNetworkQuality = RTCInstance?.networkQuality.get(String(user?.yxuid));

    const memoMembers = React.useMemo(() => {
      let arr: (MemberStruct & { sortNumber: number })[] = [];
      if (meeting) {
        arr = meeting.sortMembers.map((item) => ({
          sortNumber: (() => {
            if (item.yxUidStr === focused?.focusedYuxid && !isMobile) {
              return 11;
            }
            if (user && item.uid === user.uid) {
              return 10;
            }
            if (item.mid === meeting.hostUid) {
              return 9;
            }
            return 0;
          })(),
          ...item,
        }));
        arr.sort((a, b) => {
          if (a.sortNumber || b.sortNumber) return b.sortNumber - a.sortNumber;
          return a.inTime! - b.inTime!;
        });
      }
      return arr;
    }, [meeting, meeting?.sortMembers, user, focused?.focusedYuxid]);

    const openAudio = React.useCallback(async () => {
      if (RTCInstance?.breakLineReconnecting) {
        return;
      }
      const isOpen = RTCInstance?.isOpenAudio
        ? API.DEVICE_STATUS_REQUEST.CLOSE
        : API.DEVICE_STATUS_REQUEST.OPEN;
      try {
        try {
          await user?.quiet(String(RTCInstance?.cid), isOpen);
          await user?.updateDeviceStatus(String(RTCInstance?.cid), API.DEVICE_TYPE.AUDIO, isOpen);
          RTCInstance?.isOpenAudio ? RTCInstance.closeMic() : RTCInstance?.openMic(true);
        } catch (err: any) {
          if (err.status === 5005) {
            message.info({
              content: formatMessage({ id: 'NOT_ALLOWED_TURNED_ON_DEVICE' }),
              noIcon: true,
            });
          } else {
            message.info({
              content: err.msg || err.message,
              noIcon: true,
            });
          }
        }
      } catch (err) {
        console.log(err, 'err');
      }
    }, [RTCInstance]);

    const openVideo = React.useCallback(async () => {
      if (RTCInstance?.breakLineReconnecting) {
        return;
      }
      if (!RTCInstance?.ableDoubleScreem && RTCInstance?.isShareScreen) {
        message.info({
          content: formatMessage({ id: 'UNABLE_OPEN_VIDEO_ON_SCREEN' }),
          icon: null,
        });
        return;
      }
      try {
        await user?.updateDeviceStatus(
          String(RTCInstance?.cid),
          API.DEVICE_TYPE.VIDEO,
          RTCInstance?.isOpenVideo
            ? API.DEVICE_STATUS_REQUEST.CLOSE
            : API.DEVICE_STATUS_REQUEST.OPEN
        );
        RTCInstance?.isOpenVideo ? RTCInstance.closeCamera() : RTCInstance?.openCamera(true);
      } catch (e) {
        console.error(e);
      }
    }, [RTCInstance]);

    React.useEffect(() => {
      if (myNetworkQuality !== undefined && !myNetworkQuality) {
        if (!toastKey) {
          toastKey = Math.floor(Math.random() * 10000);
          message.info({
            content: formatMessage({ id: 'CONNECTION_IS_UNSTABLE' }),
            noIcon: true,
            key: toastKey,
            duration: 0,
          });
        }
      } else if (toastKey) {
        message.destroy(toastKey);
        toastKey = null;
      }
    }, [myNetworkQuality, formatMessage]);

    return (
      <div
        className={classnames(className, Style['people-panel'], {
          hidden: !visible,
        })}
      >
        <div className={Style.header}>
          <span className={Style.member}>
            {formatMessage({ id: 'member' })}({memoMembers?.length})
          </span>
          <HoverIcon
            name="icon-close_1"
            className={Style.close}
            onClick={() => meeting?.setPeoplePanelShow(false, true)}
          ></HoverIcon>
        </div>
        <div className={Style.scrollBox}>
          <Scrollbars renderThumbVertical={renderThumb}>
            <div className={Style['panel-wrapper']}>
              {meeting &&
                memoMembers.map((item, index) => {
                  const isHandUp = item.handupStatus === HandUpStatus.on;
                  const isMySelf = user?.uid === item.uid;
                  const remoteStream = RTCInstance?.remoteStreamsMap.get(item.yxUid);
                  const isOpenAudio =
                    (isMySelf
                      ? RTCInstance?.isOpenAudio
                      : remoteStream &&
                        remoteStream.audio &&
                        !RTCInstance?.muteMap.get(item.yxUid.toString())) ||
                    members?.interpretersAudioStatusMap.get(item.yxUid.toString()) ||
                    false;
                  const isOpenVideo =
                    (isMySelf ? RTCInstance?.isOpenVideo : remoteStream && remoteStream.video) ||
                    false;
                  // const interpreter = members?.interpretersObj[item.yxUid] || {};
                  // 同声传译开启的时候才展示译员标签
                  const interpreter =
                    simultaneous?.interpretationStatus === InterpretationStatus.starting
                      ? members?.interpretersObj[item.yxUid!] || {}
                      : {};
                  const getLanguagesLabel = () => {
                    const curLocale = getLocale();
                    const isZh = curLocale === 'zh-CN';
                    const {
                      outputChannelLanguage,
                      fromLanguageTag,
                      toLanguageTag,
                      fromLanguageNameShort,
                      toLanguageNameShort,
                    } = interpreter;
                    if (outputChannelLanguage === '') {
                      return formatMessage({ id: 'INTERPRETERS' });
                    } else if (outputChannelLanguage === fromLanguageTag) {
                      return `${formatMessage({ id: 'INTERPRETERS' })}·${
                        isZh ? fromLanguageNameShort?.charAt(0) : fromLanguageNameShort
                      }`;
                    } else if (outputChannelLanguage === toLanguageTag) {
                      return `${formatMessage({ id: 'INTERPRETERS' })}·${
                        isZh ? toLanguageNameShort?.charAt(0) : toLanguageNameShort
                      }`;
                    }
                  };
                  return (
                    <div
                      className={classnames(Style['people-item'], { [Style['my-self']]: isMySelf })}
                      key={index}
                    >
                      <div className={Style['avatar-wrapper']}>
                        <Avatar
                          style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}
                          type={isOpenAudio ? 'micOn' : 'micOff'}
                          size={isPad ? 34 : 32}
                          name={item.memberName || ''}
                          deviceType={item.deviceType}
                          url={item.memberAvatarUrl}
                        />

                        <div className={Style.info}>
                          <Tooltip
                            title={item.memberName.length > 12 ? item.memberName : ''}
                            placement="topLeft"
                          >
                            <div className={Style.wrapperName}>
                              <span className={Style.name}>{item.memberName}</span>
                              {item.deviceType === DEVICE_TYPE.WEB && (
                                <span className={Style['name-icon']}>
                                  <Icon
                                    className={Style.icon}
                                    name="icon-network"
                                    size={12}
                                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                                  />
                                </span>
                              )}
                              {[DEVICE_TYPE.IPHONE, DEVICE_TYPE.ANDROID].includes(
                                item.deviceType
                              ) && (
                                <span className={Style['name-icon']}>
                                  <Icon
                                    className={Style.icon}
                                    name="icon-phone"
                                    size={12}
                                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                                  />
                                </span>
                              )}
                            </div>
                          </Tooltip>
                          <PersonLabel
                            isList
                            isJoinHost={item.role === RoleType.COHOST}
                            isHost={item.mid === meeting.hostUid}
                            isMe={item.uid === user?.uid}
                            isTranslator={!!interpreter.uid}
                            languagesLabel={getLanguagesLabel()}
                            isFocused={
                              !isMobile && item.yxUid?.toString() === focused?.focusedYuxid
                            }
                          />
                        </div>
                      </div>

                      <div className={Style['device-status']}>
                        {isHandUp ? (
                          <img
                            src={reaction?.handupSetting?.handUpStaticWebp}
                            className={Style.handup}
                          />
                        ) : (
                          ''
                        )}
                        {(RTCInstance?.networkQuality.has(String(item.yxUid))
                          ? !RTCInstance.networkQuality.get(String(item.yxUid))
                          : false) && (
                          <Icon
                            className={`${Style['weak-network-icon']}`}
                            name="icon-signal_weak_nor"
                            size={16}
                            style={{ color: 'red' }}
                          />
                        )}
                        {meeting.screenUid === item.mid && (
                          <Icon
                            className={Style['screen-icon-wrap']}
                            name="icon-a-Mirrorshare"
                            size={16}
                          />
                        )}
                        {item.yxUid?.toString() === focused?.focusedYuxid ? (
                          <Icon
                            name="icon-focus_solid_1"
                            size={16}
                            mode="symbol"
                            className={Style.focused}
                          ></Icon>
                        ) : (
                          ''
                        )}

                        {isOpenAudio ? (
                          <Icon
                            className={`${Style['mic-icon']} ${Style['device-icon-on']}`}
                            name="icon-micro_solid"
                            size={16}
                          />
                        ) : (
                          <Icon
                            className={`${Style['mic-icon']}`}
                            name="icon-microphone_off"
                            size={16}
                            mode="symbol"
                          />
                        )}
                        {isOpenVideo ? (
                          <Icon
                            className={`${Style['video-icon']} ${Style['device-icon-on']}`}
                            name="icon-video_solid"
                            size={16}
                          />
                        ) : (
                          <Icon
                            className={`${Style['video-icon']}`}
                            name="icon-camera_off"
                            size={16}
                            mode="symbol"
                          />
                        )}
                      </div>
                      <div
                        className={`${Style['device-status-operation']} ${
                          RTCInstance?.breakLineReconnecting ? Style.disable : ''
                        }`}
                      >
                        {isOpenAudio ? (
                          <Icon
                            className={`${Style['mic-icon']} ${Style['device-status-on']}`}
                            name="icon-micro_solid"
                            size={16}
                            onClick={openAudio}
                          />
                        ) : (
                          <Icon
                            className={`${Style['mic-icon']} ${Style['device-status-on']}`}
                            name="icon-microphone_off"
                            size={16}
                            mode="symbol"
                            onClick={openAudio}
                          />
                        )}
                        {isOpenVideo ? (
                          <Icon
                            className={`${Style['video-icon']} ${Style['device-status-on']}`}
                            name="icon-video_solid"
                            size={16}
                            onClick={openVideo}
                          />
                        ) : (
                          <Icon
                            className={`${Style['video-icon']} ${Style['device-status-on']}`}
                            name="icon-camera_off"
                            mode="symbol"
                            size={16}
                            onClick={openVideo}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </Scrollbars>
        </div>
      </div>
    );
  })
);

export default PeoplePanel;
